(ns fablo-offers.common.tracking
  (:require ["mixpanel-browser" :as mixpanel]
            ["@sentry/react" :as sentry]
            [cljs-bean.core :refer [->js]]
            [clojure.string :as str]
            [fablo-offers.common.api :as api]
            [fablo-offers.common.utils :as u]
            [fablo-offers.config-static :as cs]
            [rumext.alpha :as mf]
            [tick.core :as tick]))

(def registered-trackers (atom nil))

(defn- register-tracker! [tracker-id]
  (when-not (boolean (some #{tracker-id} @registered-trackers))
    (swap! registered-trackers conj tracker-id)))

(defn- unregister-tracker! [tracker-id]
  (when (boolean (some #{tracker-id} @registered-trackers))
    (swap! registered-trackers (fn [trackers]
                                 (remove #(= tracker-id %) trackers)))))

(defn find-offer-ids-in-subtree
  [subtree]
  (some->>
   (tree-seq coll? (fn [x]
                     (if (map? x)
                       (vals x)
                       x))
             subtree)
   (filter map?)
   (keep :offer/id)))

(defn extract-sub-offers [offer]
  (some->>
   (get-in offer [:offer/params :content])
   (filter :items)
   (mapcat :items)
   (mapcat (fn [item]
             (let [ids (if (:offer/id item)
                         [(:offer/id item)]
                         (find-offer-ids-in-subtree (:content item)))]
               (map (fn [id]
                      {:id id
                       :quantity 1})
                    ids))))))

(defmulti get-tracking-params
  (fn [args]
    (:type args)))

(defmethod get-tracking-params :view-content
  [{:keys [offer provider-id]}]
  (let [{:offer/keys [id params]} offer
        sub-offers (extract-sub-offers offer)]
    (cond->
     {:provider-id provider-id
      :content-ids [id]
      :content-name (or (get-in params [:offer/name :pl])
                        (get-in offer [:offer/name :pl]))
      :content-type (if (seq sub-offers)
                      "product_group"
                      "product")}
      (seq sub-offers)
      (assoc :contents sub-offers))))

(defmethod get-tracking-params :view-content-price
  [{:keys [offer provider-id]}]
  (let [{:offer/keys [id params]} offer
        promo-price (or (:offer/promo-price params)
                        (:offer/promo-price offer))
        promo-price (when (or (not (:to promo-price))
                              (tick/> (:to promo-price) (tick/date-time)))
                      promo-price)
        price (some->> (or (:price promo-price)
                           (:offer/price params)
                           (:offer/price offer))
                       str
                       (re-find #"\d+")
                       js/parseFloat)]
    {:provider-id provider-id
     :content-ids [id]
     :content-name (or (get-in params [:offer/name :pl])
                       (get-in offer [:offer/name :pl]))
     :content-type "product"
     :price-visible true
     :value price
     :currency "PLN"}))

(defn get-purchase-tracking-params
  [{:keys [customer offer payment provider-id]}]
  (let [{:customer/keys [email phone first-name last-name]} customer
        {:payment/keys [amount]} payment]
    {:provider-id provider-id
     :content-ids [(:offer/id offer)]
     :content-name (or (get-in offer [:offer/params :offer/name :pl])
                       (get-in offer [:offer/name :pl]))
     :value amount
     :currency "PLN"
     :email email
     :phone phone
     :first-name first-name
     :last-name last-name}))

(defmethod get-tracking-params :initiate-checkout
  [args]
  (get-purchase-tracking-params args))

(defmethod get-tracking-params :purchase
  [args]
  (get-purchase-tracking-params args))

(defmethod get-tracking-params :newsletter
  [{:keys [provider-id user]}]
  {:provider-id provider-id
   :newsletter true
   :email (:email user)
   :phone (:phone user)})

(defmethod get-tracking-params :contact
  [{:keys [provider-id]}]
  {:provider-id provider-id})

(defmethod get-tracking-params :default
  [_args]
  nil)

(defn set-cookie! [k v]
  (set! js/document.cookie
        (str k "=" (or v "")
             ";domain=.fablo.eu;path=/;max-age="
             (* 60 60 24 90))))

(defn get-cookie [k]
  (let [cookies (.-cookie js/document)
        cookie-seq (str/split cookies #";")]
    (some (fn [c]
            (let [[_ name val] (re-matches #"\s?([^=]+)=(.*)" c)]
              (when (= name k)
                val)))
          cookie-seq)))

(defn track-fb-server [payload]
  (api/tx :common/track-fb payload))

(defn track-fb-browser [event-id event-name payload]
  (when (exists? js/window.fbq)
    (js/window.fbq
     "track"
     event-name
     payload
     #js {:eventID event-id})))

(def fb-browser-payload-keys
  [:content-category :content-ids :content-name :content-type :contents :currency
   :event-source-url :event-time :newsletter :num-items :price-visible :value])

(defn- fb-browser-payload [params]
  (->> (select-keys params fb-browser-payload-keys)
       (remove (comp nil? val))
       (into {})
       u/js-snake-case-keys))

(defn- fb-server-payload [params]
  (->> (assoc params
              :fbp (get-cookie "_fbp")
              :fbc (get-cookie "_fbc"))
       (remove (comp nil? val))
       (into {})))

(defn- fb-track-handler [event-name params]
  (let [event-id (u/uuid-str)
        params (merge params
                      {:event-id event-id
                       :event-name event-name
                       :event-time (some-> (js/Date.) .getTime (quot 1000))
                       :event-source-url js/location.href})]
    (track-fb-browser event-id event-name (fb-browser-payload params))
    (track-fb-server (fb-server-payload params))))

(def trackings
  {:fbq
   {:events
    {:contact "Contact"
     :initiate-checkout "InitiateCheckout"
     :newsletter "CompleteRegistration"
     :purchase "Purchase"
     :view-content "ViewContent"
     :view-content-price "ViewContent"}
    :handler fb-track-handler}})

(defn track
  ([event]
   (track event {}))
  ([event params]
   (if (string? event)
     (mixpanel/track event (->js params))
     (doseq [t @registered-trackers]
       (when-let [event-name (get-in trackings [t :events event])]
         ((get-in trackings [t :handler]) event-name params))))))

(defn use-track
  ([event] (use-track event {}))
  ([event params] (use-track (mf/deps) event params))
  ([deps event params] (use-track deps event params true))
  ([deps event params enabled?]
   (mf/use-effect
    deps
    (fn []
      (when enabled?
        (track event params))))))

(defn register [params]
  (mixpanel/register (->js params)))

(defn unregister [param]
  (mixpanel/unregister param))

(defn identify [id]
  (mixpanel/identify id))

(defn people-set [m]
  (mixpanel/people.set (->js m)))

(defn people-union [m]
  (mixpanel/people.union (->js m)))

(defn people-increment [m]
  (mixpanel/people.increment (->js m)))

(defn- get-timestamp []
  (.getTime (js/Date.)))

(defn get-url-param [param-name]
  (let [search (.. js/window -location -search)
        url-params (js/URLSearchParams. search)]
    (.get url-params param-name)))

(defn init-fb-cookies! []
  (when-let [fbclid (get-url-param "fbclid")]
    (let [fbc-val (str "fb.1." (get-timestamp) "." fbclid)]
      (set-cookie! "_fbc" fbc-val)))
  (when-not (get-cookie "_fbp")
    (let [random-int (+ 1000000000 (rand-int 8999999999))
          fbp-val (str "fb.1." (get-timestamp) "." random-int)]
      (set-cookie! "_fbp" fbp-val))))

(defn init-fb-tracker! [pixel-id]
  (js*
   "!function(b,e,v,t,s)
    {t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(document,'script',
    'https://connect.facebook.net/en_US/fbevents.js')")
  (js/window.fbq "init" pixel-id))

(defn init-tracker! [tracker-id opts]
  (register-tracker! tracker-id)
  (when (= :fbq tracker-id)
    (init-fb-cookies!)
    (init-fb-tracker! (:id opts))))

(defn current-env []
  (let [origin js/location.origin]
    (cond
      (str/includes? origin "staging") "staging"
      (str/includes? origin "app") "prod"
      :else "dev")))

(defn should-init-tracker? [tracker-envs]
  (boolean (some #{(current-env)} tracker-envs)))

(defn init-trackers!
  ([trackers]
   (init-trackers! trackers {}))
  ([trackers params]
   (when trackers
     (doseq [[tracker-id opts] trackers]
       (if-not (sequential? opts)
         (if (should-init-tracker? (:envs opts))
           (init-tracker! tracker-id (merge opts params))
           (unregister-tracker! tracker-id))
         (doseq [tracker-opts opts]
           (if (should-init-tracker? (:envs tracker-opts))
             (init-tracker! tracker-id (merge tracker-opts params))
             (unregister-tracker! tracker-id))))))))

(defn init-fb-tracker-queue! []
  (js*
   "!function(f,n)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];}(window)")
  (register-tracker! :fbq))

(defn init-default-trackers! []
  (init-fb-tracker-queue!)
  (mixpanel/init
   cs/mixpanel-token
   #js {:debug cs/DEBUG?
        :opt_out_tracking_by_default (= cs/mixpanel-token "_")
        :api_host "https://api-eu.mixpanel.com"})
  (sentry/init
   #js {:dsn "https://c723a9d5430a357a8807f8940b646821@o4507769159876608.ingest.de.sentry.io/4507769163874384"
        :integrations #js [#_(sentry/browserTracingIntegration)
                           #_(sentry/replayIntegration)]
        :tracesSampleRate 1.0
        :replaysSessionSampleRate 0.1
        :replaysOnErrorSampleRate 1.0}))

